package unlock

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.File
import org.w3c.files.FilePropertyBag
import puzzlekey.Bundle
import radar.Sparkles
import kotlin.js.Promise

/** The LootBox's steps; the CSS animates each (index.html, `.lootbox`). */
private enum class Reveal { Emerging, Shaking, Opened }

private const val EMERGE_MS = 1_000L
private const val SHAKE_MS = 1_600L

/**
 * The reveal (T16): the 📦 comes out of the glow the stars gathered into, shakes, and makes way for his
 * message and an "Open gift" button. The sky above is the size of the hunt's camera stage, so the box
 * appears where the guide was. The same reveal plays after a reload from [KeptGift].
 *
 * The certificate opens as `<img>` when it is an image; a PDF opens in a new tab (Safari's viewer, with
 * its own share button). "Keep a copy" hands it to the share sheet on iOS, or downloads it elsewhere.
 * Blob URLs are never revoked: iOS fetches them only after its own "Download?" tap.
 */
@Composable
internal fun LootBox(bundle: Bundle, onError: (String) -> Unit) {
    var reveal by remember { mutableStateOf(Reveal.Emerging) }
    var shown by remember { mutableStateOf(false) }
    val file = remember(bundle) { GiftFile(bundle) }

    LaunchedEffect(Unit) {
        delay(EMERGE_MS)
        reveal = Reveal.Shaking
        delay(SHAKE_MS)
        reveal = Reveal.Opened
    }

    Div({ classes("lootbox", reveal.name.lowercase()) }) {
        Div({ classes("sky") }) {
            Sparkles { if (reveal == Reveal.Opened) 0.35 else 0.75 }
            Div({ classes("box") }) { Text("📦") }
        }
        Div({ classes("card") }) {
            H1 { Text("Written in the stars") }
            P({ classes("message") }) { Text(bundle.message) }
            if (!file.isImage || !shown) {
                Button({
                    classes("pill")
                    if (reveal != Reveal.Opened) disabled()
                    onClick {
                        // Synchronous in the tap: Safari only lets a tap open a tab.
                        if (file.isImage) shown = true else file.open()
                    }
                }) { Text("Open gift") }
            }
            if (file.isImage && shown) Img(file.url, attrs = { classes("gift"); alt("Your gift") })
            if (shown || !file.isImage) TextLink("Keep a copy") { file.keep(onError) }
        }
    }
}

/** The certificate as a file: a blob URL to show or open it, and a way to keep it. */
private class GiftFile(bundle: Bundle) {
    val isImage = bundle.mimeType.startsWith("image/")
    private val blob = Blob(arrayOf(bundle.cert), BlobPropertyBag(type = bundle.mimeType))
    val url: String = URL.createObjectURL(blob)
    private val name = "gift." + (EXTENSIONS[bundle.mimeType] ?: "bin")
    private val mimeType = bundle.mimeType

    /** A new tab with the PDF; if Safari blocks it, this tab goes there (Back reopens the gift from [KeptGift]). */
    fun open() {
        if (window.open(url, "_blank") == null) window.location.href = url
    }

    /** The share sheet where it takes files (iOS: Save to Files, Photos, AirDrop), else a download. */
    fun keep(onError: (String) -> Unit) {
        val navigator = window.navigator.asDynamic()
        val shared = File(arrayOf(blob), name, FilePropertyBag(type = mimeType))
        val data = js("({})")
        data.files = arrayOf(shared)
        if (navigator.canShare != undefined && navigator.canShare(data) == true) {
            navigator.share(data).unsafeCast<Promise<Any?>>().catch { e ->
                // AbortError: she closed the sheet.
                if (e.asDynamic().name != "AbortError") onError("share: ${e.asDynamic().message ?: e}")
            }
            return
        }
        val link = document.createElement("a") as HTMLAnchorElement
        link.href = url
        link.download = name
        link.click()
    }

    private companion object {
        val EXTENSIONS = mapOf(
            "application/pdf" to "pdf",
            "image/png" to "png",
            "image/jpeg" to "jpg",
            "image/gif" to "gif",
            "image/webp" to "webp",
            "image/heic" to "heic",
        )
    }
}
